/* Presentational building blocks for the /docs section. Kept in a component file
 * (not a route) so nothing server-only leaks into the client bundle. The look
 * mirrors the marketing landing: #0b0c0e ground, hairline white borders, indigo
 * accent, mono code panels. */
import type { ReactNode } from 'react'

export const PANEL = 'rounded-2xl border border-white/10 bg-white/[0.02]'

/** A monospace code panel (matches the landing's codeBlock token). */
export function Code({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div className="overflow-hidden rounded-lg border border-white/10 bg-[#0e1013] shadow-2xl shadow-black/40">
      {label ? (
        <div className="border-b border-white/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          {label}
        </div>
      ) : null}
      <pre className="overflow-x-auto px-4 py-3 font-mono text-[12.5px] leading-relaxed text-zinc-100">
        {children}
      </pre>
    </div>
  )
}

const METHOD_TONE: Record<string, string> = {
  GET: 'text-emerald-300 ring-emerald-400/30 bg-emerald-500/10',
  POST: 'text-indigo-300 ring-indigo-400/30 bg-indigo-500/10',
  PATCH: 'text-amber-300 ring-amber-400/30 bg-amber-500/10',
  DELETE: 'text-rose-300 ring-rose-400/30 bg-rose-500/10',
}

/** A method + path header for an API endpoint. */
export function Method({ verb, path }: { verb: string; path: string }) {
  return (
    <div className="flex flex-wrap items-center gap-3 font-mono text-sm">
      <span
        className={`inline-flex rounded px-2 py-0.5 text-[11px] font-bold uppercase ring-1 ring-inset ${
          METHOD_TONE[verb] ?? 'text-zinc-300 ring-white/15 bg-white/5'
        }`}
      >
        {verb}
      </span>
      <span className="text-zinc-100">{path}</span>
    </div>
  )
}

/** A small rounded tag. */
export function Pill({ children, tone = 'zinc' }: { children: ReactNode; tone?: 'zinc' | 'indigo' | 'rose' }) {
  const tones = {
    zinc: 'bg-white/5 text-zinc-300 ring-white/10',
    indigo: 'bg-indigo-500/10 text-indigo-300 ring-indigo-400/30',
    rose: 'bg-rose-500/10 text-rose-300 ring-rose-400/30',
  }
  return (
    <span className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${tones[tone]}`}>
      {children}
    </span>
  )
}

/** A documented entry (endpoint, command, or tool) in a panel. */
export function Entry({
  id,
  header,
  desc,
  pills,
  children,
}: {
  id?: string
  header: ReactNode
  desc?: ReactNode
  pills?: ReactNode
  children?: ReactNode
}) {
  return (
    <div id={id} className={`scroll-mt-24 ${PANEL} p-5 sm:p-6`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {header}
        {pills ? <div className="flex flex-wrap gap-2">{pills}</div> : null}
      </div>
      {desc ? <p className="mt-3 text-sm text-zinc-400">{desc}</p> : null}
      {children ? <div className="mt-4 space-y-4">{children}</div> : null}
    </div>
  )
}

/** A params/fields table. rows = [name, type, description]. */
export function ParamTable({ rows, head = 'Parameter' }: { rows: [string, string, string][]; head?: string }) {
  if (!rows.length) return null
  return (
    <div className="overflow-x-auto rounded-lg border border-white/10">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-zinc-500">
            <th className="px-3 py-2 font-semibold">{head}</th>
            <th className="px-3 py-2 font-semibold">Type</th>
            <th className="px-3 py-2 font-semibold">Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([name, type, note]) => (
            <tr key={name} className="border-b border-white/5 last:border-0 align-top">
              <td className="whitespace-nowrap px-3 py-2 font-mono text-[12.5px] text-zinc-200">{name}</td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-[12px] text-zinc-400">{type}</td>
              <td className="px-3 py-2 text-zinc-400">{note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** A page section with an eyebrow + heading. */
export function Section({
  id,
  eyebrow,
  title,
  intro,
  children,
}: {
  id?: string
  eyebrow?: string
  title: string
  intro?: ReactNode
  children: ReactNode
}) {
  return (
    <section id={id} className="scroll-mt-24">
      {eyebrow ? (
        <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300">{eyebrow}</p>
      ) : null}
      <h2 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">{title}</h2>
      {intro ? <div className="mt-3 max-w-2xl text-zinc-400">{intro}</div> : null}
      <div className="mt-6 space-y-4">{children}</div>
    </section>
  )
}
